import { Card, CardContent, CardHeader, CardTitle } from '@klear/ui/components/card'
import { useCustomerOfferForecasts } from '@klear/customer-ui/use-customers'
import {
  offerForecastConfidence,
  offerForecastDaysHeadline,
  offerForecastLatenessBand
} from '@klear/invoice-offer/offer-forecast'

type CustomerOfferForecastCardProps = {
  customerId: number
  traderId?: number
}

export function CustomerOfferForecastCard({ customerId, traderId }: CustomerOfferForecastCardProps) {
  const { forecastsByCustomerId, isLoading } = useCustomerOfferForecasts([customerId], { traderId })
  const forecast = forecastsByCustomerId.get(customerId)

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-6 text-sm text-muted-foreground">Loading payment forecast…</CardContent>
      </Card>
    )
  }

  if (forecast == null || forecast.sampleSize === 0) {
    return null
  }

  const band = offerForecastLatenessBand(forecast.median)
  const confidence = offerForecastConfidence(forecast.sampleSize)
  const earlyPct = forecast.earlyShare != null ? Math.round(forecast.earlyShare * 100) : null
  const p25 = forecast.p25 != null ? Math.round(forecast.p25) : null
  const p75 = forecast.p75 != null ? Math.round(forecast.p75) : null
  const isLate = (forecast.median ?? 0) > 0
  const isEarly = (forecast.median ?? 0) < 0

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-medium">Payment Behavior</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <p
            className={
              isLate
                ? 'text-xl font-semibold tracking-tight text-orange-500'
                : isEarly
                  ? 'text-xl font-semibold tracking-tight text-emerald-600'
                  : 'text-xl font-semibold tracking-tight'
            }
          >
            {offerForecastDaysHeadline(forecast.median)}
          </p>
          {band != null && (
            <p className="mt-1 text-sm text-muted-foreground">
              expected at payment ·{' '}
              <span
                className={
                  isLate ? 'font-medium text-orange-500' : isEarly ? 'font-medium text-emerald-600' : 'font-medium'
                }
              >
                {band}
              </span>
            </p>
          )}
        </div>

        {p25 != null && p75 != null && (
          <p className="text-sm">
            <span className="text-muted-foreground">Likely range (p25–p75): </span>
            <span className="font-medium">
              {p25} to {p75} days
            </span>
          </p>
        )}

        <p className="text-sm text-muted-foreground">
          History: {forecast.sampleSize} closed order{forecast.sampleSize === 1 ? '' : 's'} · {confidence} confidence
        </p>

        {earlyPct != null && <p className="text-sm text-muted-foreground">paid early {earlyPct}% of the time</p>}

        {forecast.openExposure.amount !== 0n && (
          <p className="text-sm">
            <span className="text-muted-foreground">Open exposure: </span>
            <span className="font-medium">{forecast.openExposure.toString({ notation: 'compact' })}</span>
          </p>
        )}
      </CardContent>
    </Card>
  )
}
